import { inject, Injectable } from '@angular/core';
import { PortfolioMilestone } from '@models';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import type { FilterState as StudyFilterState } from '../study-filter/study-filter.service';
import { StudyFilterService } from '../study-filter/study-filter.service';

type FilterList = Record<string, Set<string>>;
type FilterSource = 'table' | 'chart' | 'kpi';
type FiltersBySource = Record<FilterSource, FilterList>;

interface FilterEvent {
  source: FilterSource;
  field: string;
  value: string;
  action: 'add' | 'remove' | 'clear';
}

interface FilterState {
  // Union of all contributor filters, used for UI display.
  appliedFilters: FilterList;
  // AND-merged filters across contributors, used for data filtering.
  mergedAppliedFilters: FilterList;
  appliedFiltersBySource: FiltersBySource;
  availableOptions: FilterList;
  filteredData: PortfolioMilestone[];
  totalFilterCount: number;
  // Filters currently applied in StudyFilterService.
  appliedStudyFilters: FilterList;
}

@Injectable({
  providedIn: 'root'
})
export class PortfolioMilestoneFilterService {
  private studyFilterService = inject(StudyFilterService);

  private sourceData: PortfolioMilestone[] = [];
  private allowedStudySks = new Set<string>();
  private studyFilterSubscription: Subscription | null = null;
  private readonly filterSources: FilterSource[] = ['table', 'chart', 'kpi'];

  // Snapshot of study filters before temporary panel mutations.
  private originalStudyFiltersSnapshot: FilterList | null = null;

  private _filterState = new BehaviorSubject<FilterState>({
    appliedFilters: {},
    mergedAppliedFilters: {},
    appliedFiltersBySource: this.createEmptyFiltersBySource(),
    availableOptions: {},
    filteredData: [],
    totalFilterCount: 0,
    appliedStudyFilters: {}
  });

  private _filterEvents = new BehaviorSubject<FilterEvent | null>(null);

  // Public observables.
  filterState$: Observable<FilterState> = this._filterState.asObservable();
  filterEvents$: Observable<FilterEvent | null> = this._filterEvents.asObservable();

  /**
   * Initialize the filter service with milestone data and keep it synchronized
   * with global study filters from StudyFilterService.
   */
  initializeFilters(milestones: PortfolioMilestone[]): void {
    this.sourceData = milestones;

    if (!this.studyFilterSubscription) {
      this.studyFilterSubscription = this.studyFilterService.filterState$.subscribe((state) => {
        this.updateFromStudyFilterState(state);
      });
    }

    this.updateFromStudyFilterState(this.studyFilterService.getCurrentState());
  }

  private updateFromStudyFilterState(studyFilterState: StudyFilterState): void {
    const filteredStudies = studyFilterState.filteredData;
    const appliedStudyFilters = studyFilterState.appliedFilters;

    if (filteredStudies && filteredStudies.length > 0) {
      this.allowedStudySks = new Set(filteredStudies.map((study) => String(study.studySk)));
    } else if (Array.isArray(this.sourceData)) {
      this.allowedStudySks = new Set(this.sourceData.map((milestone) => String(milestone.studySk)));
    } else {
      this.allowedStudySks = new Set();
    }

    const currentState = this._filterState.value;
    const filteredData = this.applyFilters(this.sourceData, currentState.mergedAppliedFilters);

    this._filterState.next({
      ...currentState,
      filteredData,
      availableOptions: this.calculateAvailableOptions(filteredData),
      appliedStudyFilters: this.cloneFilterList(appliedStudyFilters)
    });
  }

  /**
   * Save current StudyFilterService state to restore it later.
   */
  saveOriginalStudyFilters(): void {
    if (this.originalStudyFiltersSnapshot) {
      return;
    }

    const currentStudyFilters = this.studyFilterService.getCurrentState().appliedFilters;
    this.originalStudyFiltersSnapshot = this.cloneFilterList(currentStudyFilters);
  }

  /**
   * Restore previously saved StudyFilterService state.
   */
  restoreOriginalStudyFilters(): void {
    if (!this.originalStudyFiltersSnapshot) {
      return;
    }

    this.studyFilterService.setFilters(
      this.cloneFilterList(this.originalStudyFiltersSnapshot),
      'table'
    );
    this.originalStudyFiltersSnapshot = null;
  }

  private ensureOriginalStudyFiltersSaved(): void {
    this.saveOriginalStudyFilters();
  }

  /**
   * Proxy helpers to mutate StudyFilterService and keep its state mirrored here.
   */
  addStudyFilter(field: string, value: string, source: FilterSource = 'table'): void {
    this.ensureOriginalStudyFiltersSaved();
    this.studyFilterService.addFilter(field, value, source);
    this.syncStudyFilters();
  }

  removeStudyFilter(field: string, value: string, source: FilterSource = 'table'): void {
    this.ensureOriginalStudyFiltersSaved();
    this.studyFilterService.removeFilter(field, value, source);
    this.syncStudyFilters();
  }

  clearAllStudyFilters(source: FilterSource = 'table'): void {
    this.ensureOriginalStudyFiltersSaved();
    this.studyFilterService.clearAllFilters(source);
    this.syncStudyFilters();
  }

  clearStudyFieldFilters(field: string, source: FilterSource = 'table'): void {
    this.ensureOriginalStudyFiltersSaved();
    this.studyFilterService.clearFieldFilters(field, source);
    this.syncStudyFilters();
  }

  private syncStudyFilters(): void {
    const currentState = this._filterState.value;
    const appliedStudyFilters = this.studyFilterService.getCurrentState().appliedFilters;

    if (this.areFilterListsEqual(currentState.appliedStudyFilters, appliedStudyFilters)) {
      return;
    }

    this._filterState.next({
      ...currentState,
      appliedStudyFilters: this.cloneFilterList(appliedStudyFilters)
    });
  }

  /**
   * Add a source-specific filter.
   */
  addFilter(field: string, value: string, source: FilterSource = 'table'): void {
    const currentState = this._filterState.value;
    const updatedFiltersBySource = this.cloneFiltersBySource(currentState.appliedFiltersBySource);
    const sourceFilters = updatedFiltersBySource[source];

    if (!sourceFilters[field]) {
      sourceFilters[field] = new Set();
    }
    sourceFilters[field].add(value);

    this.updateFilterState(updatedFiltersBySource, source, field, value, 'add');
  }

  /**
   * Remove a source-specific filter.
   */
  removeFilter(field: string, value: string, source: FilterSource = 'table'): void {
    const currentState = this._filterState.value;
    const updatedFiltersBySource = this.cloneFiltersBySource(currentState.appliedFiltersBySource);
    const sourceFilters = updatedFiltersBySource[source];
    const fieldFilters = sourceFilters[field];

    if (fieldFilters) {
      this.removeEquivalentValueFromSet(fieldFilters, field, value);
      if (fieldFilters.size === 0) {
        delete sourceFilters[field];
      }
    }

    this.updateFilterState(updatedFiltersBySource, source, field, value, 'remove');
  }

  /**
   * Remove a filter value from every contributor source.
   */
  removeFilterFromAllSources(field: string, value: string, source: FilterSource = 'kpi'): void {
    const currentState = this._filterState.value;
    const updatedFiltersBySource = this.cloneFiltersBySource(currentState.appliedFiltersBySource);
    let removedAny = false;

    this.filterSources.forEach((filterSource) => {
      const sourceFilters = updatedFiltersBySource[filterSource];
      const fieldFilters = sourceFilters[field];
      if (!fieldFilters) {
        return;
      }

      const removed = this.removeEquivalentValueFromSet(fieldFilters, field, value);
      if (removed) {
        removedAny = true;
      }

      if (fieldFilters.size === 0) {
        delete sourceFilters[field];
      }
    });

    if (!removedAny) {
      return;
    }

    this.updateFilterState(updatedFiltersBySource, source, field, value, 'remove');
  }

  /**
   * Clear all filters for one contributor source.
   */
  clearAllFilters(source: FilterSource = 'table'): void {
    const currentState = this._filterState.value;
    const updatedFiltersBySource = this.cloneFiltersBySource(currentState.appliedFiltersBySource);
    updatedFiltersBySource[source] = {};

    this.updateFilterState(updatedFiltersBySource, source, '', '', 'clear');
  }

  /**
   * Clear all panel-local filters from every source.
   */
  clearAllPanelFilters(source: FilterSource = 'kpi'): void {
    this.updateFilterState(this.createEmptyFiltersBySource(), source, '', '', 'clear');
  }

  /**
   * Clear all filters for one field in one source.
   */
  clearFieldFilters(field: string, source: FilterSource = 'table'): void {
    const currentState = this._filterState.value;
    const updatedFiltersBySource = this.cloneFiltersBySource(currentState.appliedFiltersBySource);
    delete updatedFiltersBySource[source][field];

    this.updateFilterState(updatedFiltersBySource, source, field, '', 'clear');
  }

  /**
   * Replace all filters for one source (used by table header menu).
   */
  setFilters(filters: FilterList, source: FilterSource = 'table'): void {
    const currentState = this._filterState.value;
    const updatedFiltersBySource = this.cloneFiltersBySource(currentState.appliedFiltersBySource);
    updatedFiltersBySource[source] = this.cloneFilterList(filters);

    this.updateFilterState(updatedFiltersBySource, source, '', '', 'add');
  }

  /**
   * Get current filters for one contributor source.
   */
  getFiltersForSource(source: FilterSource): FilterList {
    return this.cloneFilterList(this._filterState.value.appliedFiltersBySource[source]);
  }

  /**
   * Get UI display filters (union across sources).
   */
  getDisplayFilters(): FilterList {
    return this.cloneFilterList(this._filterState.value.appliedFilters);
  }

  /**
   * Returns true when at least one panel-local filter is active.
   */
  hasActivePanelFilters(): boolean {
    return this._filterState.value.totalFilterCount > 0;
  }

  /**
   * Get current filter state.
   */
  getCurrentState(): FilterState {
    return this._filterState.value;
  }

  /**
   * Apply AND-merged filters to source data.
   */
  private applyFilters(data: PortfolioMilestone[], filters: FilterList): PortfolioMilestone[] {
    if (!Array.isArray(data)) {
      return [];
    }
    return data.filter((milestone) => {
      if (!this.allowedStudySks.has(String(milestone.studySk))) {
        return false;
      }

      return Object.entries(filters).every(([field, values]) => {
        // Empty set means contributors conflict for this field (no intersection).
        if (values.size === 0) {
          return false;
        }

        const milestoneValue = milestone[field as keyof PortfolioMilestone];
        if (milestoneValue === null || milestoneValue === undefined) {
          return false;
        }

        return values.has(String(milestoneValue));
      });
    });
  }

  /**
   * Calculate available filter options based on current data.
   */
  private calculateAvailableOptions(data: PortfolioMilestone[]): FilterList {
    const options: FilterList = {};
    const fields: (keyof PortfolioMilestone)[] = [
      'parentClientName',
      'protocolNumber',
      'status',
      'milestoneName'
    ];

    fields.forEach((field) => {
      options[field] = new Set();
      data.forEach((milestone) => {
        const value = milestone[field];
        if (value !== null && value !== undefined && value !== 'NULL') {
          options[field].add(String(value));
        }
      });
    });

    return options;
  }

  /**
   * Count active UI filters.
   */
  private calculateTotalFilters(filters: FilterList): number {
    return Object.values(filters).reduce((sum, set) => sum + set.size, 0);
  }

  private updateFilterState(
    newFiltersBySource: FiltersBySource,
    source: FilterSource,
    field: string,
    value: string,
    action: 'add' | 'remove' | 'clear'
  ): void {
    const currentState = this._filterState.value;

    if (this.areFiltersBySourceEqual(currentState.appliedFiltersBySource, newFiltersBySource)) {
      this.syncStudyFilters();
      return;
    }

    const mergedAppliedFilters = this.mergeFiltersBySourceWithAnd(newFiltersBySource);
    const displayFilters = this.mergeFiltersBySourceForDisplay(newFiltersBySource);
    const filteredData = this.applyFilters(this.sourceData, mergedAppliedFilters);

    this._filterState.next({
      ...currentState,
      appliedFiltersBySource: this.cloneFiltersBySource(newFiltersBySource),
      mergedAppliedFilters,
      appliedFilters: displayFilters,
      availableOptions: this.calculateAvailableOptions(filteredData),
      filteredData,
      totalFilterCount: this.calculateTotalFilters(displayFilters)
    });

    this._filterEvents.next({ source, field, value, action });
    this.syncStudyFilters();
  }

  private createEmptyFiltersBySource(): FiltersBySource {
    return {
      table: {},
      chart: {},
      kpi: {}
    };
  }

  private cloneFiltersBySource(filtersBySource: FiltersBySource): FiltersBySource {
    return {
      table: this.cloneFilterList(filtersBySource.table),
      chart: this.cloneFilterList(filtersBySource.chart),
      kpi: this.cloneFilterList(filtersBySource.kpi)
    };
  }

  private cloneFilterList(filters: FilterList): FilterList {
    const cloned: FilterList = {};

    Object.entries(filters ?? {}).forEach(([field, values]) => {
      cloned[field] = new Set(Array.from(values ?? []));
    });

    return cloned;
  }

  private mergeFiltersBySourceForDisplay(filtersBySource: FiltersBySource): FilterList {
    const merged: FilterList = {};

    this.filterSources.forEach((source) => {
      const sourceFilters = filtersBySource[source];
      Object.entries(sourceFilters).forEach(([field, values]) => {
        if (!merged[field]) {
          merged[field] = new Set<string>();
        }
        values.forEach((entry) => merged[field].add(entry));
      });
    });

    return merged;
  }

  private mergeFiltersBySourceWithAnd(filtersBySource: FiltersBySource): FilterList {
    const merged: FilterList = {};
    const fields = new Set<string>();

    this.filterSources.forEach((source) => {
      Object.keys(filtersBySource[source]).forEach((field) => fields.add(field));
    });

    fields.forEach((field) => {
      const filtersForField = this.filterSources
        .map((source) => filtersBySource[source][field])
        .filter((values): values is Set<string> => !!values);

      if (filtersForField.length === 0) {
        return;
      }

      const mergedValues = new Set(Array.from(filtersForField[0]));
      filtersForField.slice(1).forEach((values) => {
        Array.from(mergedValues).forEach((entry) => {
          if (!values.has(entry)) {
            mergedValues.delete(entry);
          }
        });
      });

      merged[field] = mergedValues;
    });

    return merged;
  }

  private removeEquivalentValueFromSet(
    filters: Set<string>,
    field: string,
    value: string
  ): boolean {
    if (field !== 'status') {
      return filters.delete(value);
    }

    const normalizedTarget = this.normalizeStatusValue(value);
    let removed = false;

    Array.from(filters).forEach((filterValue) => {
      if (this.normalizeStatusValue(filterValue) === normalizedTarget) {
        filters.delete(filterValue);
        removed = true;
      }
    });

    return removed;
  }

  private normalizeStatusValue(value: string): string {
    return value.trim().replace(/\s+/g, ' ').toLowerCase();
  }

  private areFiltersBySourceEqual(
    currentFiltersBySource: FiltersBySource,
    nextFiltersBySource: FiltersBySource
  ): boolean {
    return this.filterSources.every((source) =>
      this.areFilterListsEqual(currentFiltersBySource[source], nextFiltersBySource[source])
    );
  }

  private areFilterListsEqual(currentFilters: FilterList, nextFilters: FilterList): boolean {
    const currentFields = Object.keys(currentFilters ?? {});
    const nextFields = Object.keys(nextFilters ?? {});

    if (currentFields.length !== nextFields.length) {
      return false;
    }

    for (const field of currentFields) {
      const currentValues = currentFilters[field];
      const nextValues = nextFilters[field];

      if (!nextValues || currentValues.size !== nextValues.size) {
        return false;
      }

      for (const value of currentValues) {
        if (!nextValues.has(value)) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Return current study-level filters from StudyFilterService.
   */
  getCurrentStudyFilters(): FilterList {
    return this.studyFilterService.getCurrentState().appliedFilters;
  }
}
